/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, SimpleChanges,
} from '@angular/core';
import { FeedStoreService } from '../../services/feed-store.service';
import { toPreviewHtml } from '../../common/community/feed/preview-html';
import { CommunityProfileActivityKind, CommunityProfileCard, toCommunityProfileActivityCard } from '../activity-card/community-profile-activity';

const EMPTY_COPY: Record<CommunityProfileActivityKind, { own: string; other: string }> = {
  comments: { own: 'You haven\'t commented on anything yet', other: '{name} hasn\'t commented on anything yet' },
  mentions: { own: 'Nobody has mentioned you yet', other: 'Nobody has mentioned {name} yet' },
  likes: { own: 'You haven\'t liked anything yet', other: '' },
};

// The Comments, Mentions and Likes tabs, as feed-style cards (ut-community-profile-activity-card).
@Component({
  selector: 'ut-community-profile-activity-list',
  templateUrl: './community-profile-activity-list.component.html',
  styleUrls: ['./community-profile-activity-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfileActivityListComponent implements OnChanges {
  @Input({ required: true }) user: any;
  @Input({ required: true }) kind: CommunityProfileActivityKind;
  @Input() isOwn = false;

  store: any;
  rows: CommunityProfileCard[] = [];

  constructor(
    @Inject('UserService') private UserService: any,
    @Inject('$filter') private $filter: any,
    private feedStore: FeedStoreService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Only a new user or list reloads; isOwn changes once me() resolves.
    if (!changes.user && !changes.kind) return;
    // Drop the old cards now, or the next tab briefly shows their avatars.
    this.rows = [];
    const userId = this.user.id;
    const fetchers: Record<CommunityProfileActivityKind, (params: any) => any> = {
      comments: (params) => this.UserService.comments(userId, params),
      mentions: (params) => this.UserService.mentions(userId, params),
      likes: (params) => this.UserService.likes('me', params),
    };
    this.store = this.feedStore.create(fetchers[this.kind]);
    this.track(this.store.load());
  }

  get emptyTitle(): string {
    const copy = EMPTY_COPY[this.kind];
    return this.isOwn ? copy.own : copy.other.replace('{name}', this.user.name);
  }

  loadMore(): void {
    this.track(this.store.loadMore());
  }

  private refresh(): void {
    const options = {
      owner: this.user,
      isOwn: this.isOwn,
      toText: (content: string) => toPreviewHtml(this.$filter('uMarkdown')(content || ''), this.$filter),
    };
    this.rows = this.store.feedItems
      .map((entry) => toCommunityProfileActivityCard(this.kind, entry, options))
      .filter(Boolean);
    this.cdr.markForCheck();
  }

  // FeedStore mutates in place, so re-render when a page starts and when it lands.
  private track(promise: any): void {
    this.cdr.markForCheck();
    promise.then(() => this.refresh());
  }
}
